import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { FolderPlus, Folder, Trash2, ChevronDown, ChevronRight, Star, FolderInput } from "lucide-react";
import {
  listScanCollections, createScanCollection, deleteScanCollection,
  listScanFavorites, deleteScanFavorite, saveScanFavorite,
} from "../../lib/api";

const EMOJIS = ["📁", "🤖", "🏪", "🌴", "📱", "💻", "💰", "🚀"];

const IdeaMini = ({ fav, collections, onDelete, onMove }) => {
  const idea = fav.idea || {};
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-start gap-3" data-testid={`collection-fav-${fav.id}`}>
      {idea.photo_ref ? (
        <img src={idea.photo_ref} alt="" className="w-12 h-12 rounded-lg object-cover border border-gray-100 shrink-0" />
      ) : (
        <span className="w-12 h-12 rounded-lg bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center text-sm font-extrabold shrink-0">{idea.ai_score || "★"}</span>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-display font-semibold text-[#0F0F0F] text-sm truncate">{idea.name || "Saved idea"}</p>
        {idea.description && <p className="text-xs text-[#687280] mt-0.5 line-clamp-2">{idea.description}</p>}
        <div className="flex items-center gap-2 mt-2">
          <select
            value={fav.collection_id || ""}
            onChange={(e) => onMove(fav, e.target.value)}
            className="text-[11px] font-semibold border border-gray-200 rounded-full px-2 py-1 outline-none focus:ring-1 focus:ring-[#D4AF37] bg-white text-[#374151]"
            data-testid={`collection-move-${fav.id}`}
          >
            <option value="">📥 Unsorted</option>
            {collections.map((c) => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
          </select>
        </div>
      </div>
      <button onClick={() => onDelete(fav.id)} className="text-[#687280] hover:text-red-500 transition-colors shrink-0" data-testid={`collection-remove-${fav.id}`} title="Remove">
        <Trash2 size={15} />
      </button>
    </div>
  );
};

export const NotebookCollections = () => {
  const [collections, setCollections] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("📁");
  const [open, setOpen] = useState({});

  const load = useCallback(() => {
    Promise.all([listScanCollections(), listScanFavorites()])
      .then(([cols, favs]) => { setCollections(cols); setFavorites(favs); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addFolder = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const c = await createScanCollection(name.trim(), emoji);
      setCollections((p) => [c, ...p]);
      setName(""); setEmoji("📁");
      setOpen((o) => ({ ...o, [c.id]: true }));
      toast.success("Folder created");
    } catch { toast.error("Could not create folder"); }
  };

  const removeFolder = async (cid) => {
    setCollections((p) => p.filter((c) => c.id !== cid));
    setFavorites((p) => p.map((f) => (f.collection_id === cid ? { ...f, collection_id: "" } : f)));
    try { await deleteScanCollection(cid); } catch { load(); }
  };

  const removeFav = async (fid) => {
    setFavorites((p) => p.filter((f) => f.id !== fid));
    try { await deleteScanFavorite(fid); } catch { load(); }
  };

  const moveFav = async (fav, collectionId) => {
    if ((fav.collection_id || "") === collectionId) return;
    try {
      await deleteScanFavorite(fav.id);
      const created = await saveScanFavorite(fav.idea, collectionId);
      setFavorites((p) => p.map((f) => (f.id === fav.id ? created : f)));
      toast.success(collectionId ? "Moved to folder" : "Moved to Unsorted");
    } catch { toast.error("Could not move"); load(); }
  };

  const favsOf = (cid) => favorites.filter((f) => (f.collection_id || "") === cid);
  const unsorted = favsOf("");
  const groups = [...collections.map((c) => ({ ...c, kind: "folder" })), { id: "", name: "Unsorted", emoji: "📥", kind: "unsorted" }];

  return (
    <div className="max-w-3xl space-y-6" data-testid="notebook-collections">
      {/* Create folder */}
      <form onSubmit={addFolder} className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          {EMOJIS.map((e) => (
            <button type="button" key={e} onClick={() => setEmoji(e)} className={`w-8 h-8 rounded-lg text-base transition-colors ${emoji === e ? "bg-[#FBF7EA] ring-2 ring-[#D4AF37]" : "hover:bg-gray-50"}`} data-testid={`collection-emoji-${e}`}>{e}</button>
          ))}
        </div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New folder name (e.g. Passive Income)"
          className="flex-1 min-w-[180px] bg-[#F7F7F7] rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]"
          data-testid="collection-name-input"
        />
        <button type="submit" className="inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#D4AF37] hover:text-[#0F0F0F] transition-colors" data-testid="collection-create-btn">
          <FolderPlus size={16} /> Create
        </button>
      </form>

      {favorites.length === 0 && collections.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-14 px-8 text-center">
          <p className="text-[#687280]">No collections yet. Create a folder above, then save scanned ideas from the Idea Hub to organize them here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const items = favsOf(g.id);
            if (g.kind === "unsorted" && items.length === 0) return null;
            const isOpen = open[g.id] ?? (g.kind === "unsorted");
            return (
              <div key={g.id || "unsorted"} className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid={`collection-folder-${g.id || "unsorted"}`}>
                <div className="flex items-center gap-3 px-5 py-4">
                  <button onClick={() => setOpen((o) => ({ ...o, [g.id]: !isOpen }))} className="flex items-center gap-3 flex-1 text-left" data-testid={`collection-toggle-${g.id || "unsorted"}`}>
                    {isOpen ? <ChevronDown size={18} className="text-[#9aa1ab]" /> : <ChevronRight size={18} className="text-[#9aa1ab]" />}
                    <span className="text-lg">{g.emoji}</span>
                    <span className="font-display font-bold text-[#0F0F0F]">{g.name}</span>
                    <span className="text-xs bg-gray-100 text-[#687280] rounded-full px-2 py-0.5 font-semibold">{items.length}</span>
                  </button>
                  {g.kind === "folder" && (
                    <button onClick={() => removeFolder(g.id)} className="text-[#687280] hover:text-red-500 transition-colors" data-testid={`collection-delete-folder-${g.id}`} title="Delete folder">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                {isOpen && (
                  <div className="px-5 pb-5 space-y-3">
                    {items.length === 0 ? (
                      <p className="text-sm text-[#9aa1ab] flex items-center gap-2"><FolderInput size={15} /> Empty — move ideas here using the dropdown on any saved idea.</p>
                    ) : (
                      items.map((f) => <IdeaMini key={f.id} fav={f} collections={collections} onDelete={removeFav} onMove={moveFav} />)
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
